type TypedArrayConstructor = { readonly BYTES_PER_ELEMENT: number; new(buffer: ArrayBufferLike, byteOffset?: number, length?: number): Int8Array | Uint8Array | Uint8ClampedArray | Int16Array | Uint16Array | Int32Array | Uint32Array | Float32Array | Float64Array | BigInt64Array | BigUint64Array };

type IntlWithOptionalConstructors = typeof Intl & { ListFormat?: new(locales?: string | string[], options?: Intl.ListFormatOptions) => Intl.ListFormat; RelativeTimeFormat?: new(locales?: string | string[], options?: Intl.RelativeTimeFormatOptions) => Intl.RelativeTimeFormat };

/**
 * The five primitive wrapper categories, each identified by one internal slot.
 */
type BoxedPrimitiveKind = "boxedBoolean" | "boxedNumber" | "boxedString" | "boxedBigInt" | "boxedSymbol";

/**
 * The Intl formatter categories, each rebuilt from its resolved options.
 */
type IntlFormatterKind = "collator" | "dateTimeFormat" | "numberFormat" | "listFormat" | "relativeTimeFormat";

/**
 * The strategy used to reproduce one category of object.
 *
 * @remarks
 * `reference` covers values whose state has no userland accessor, so the
 * original is the only usable result. Every other member names a builder.
 *
 * The two sub-unions exist so that `cloneBoxed` and `cloneIntl` accept only
 * the kinds they handle. Their switches are then exhaustive without a
 * `default` branch, and adding a member here fails to compile until every
 * switch over it gains a case.
 */
type CloneKind = "reference" | "array" | "map" | "set" | "date" | "regexp" | "error" | "promise" | "arrayBuffer" | "sharedArrayBuffer" | "view" | "url" | "urlSearchParams" | BoxedPrimitiveKind | IntlFormatterKind;

const objectToString = Function.prototype.call.bind(Object.prototype.toString) as (input: unknown) => string;
const booleanValueOf = Function.prototype.call.bind(Boolean.prototype.valueOf) as (input: object) => boolean;
const numberValueOf = Function.prototype.call.bind(Number.prototype.valueOf) as (input: object) => number;
const stringValueOf = Function.prototype.call.bind(String.prototype.valueOf) as (input: object) => string;
const symbolValueOf = Function.prototype.call.bind(Symbol.prototype.valueOf) as (input: object) => symbol;
const bigintValueOf = Function.prototype.call.bind(BigInt.prototype.valueOf) as (input: object) => bigint;
const dateGetTime = Function.prototype.call.bind(Date.prototype.getTime) as (input: object) => number;
const isPrototypeOf = Function.prototype.call.bind(Object.prototype.isPrototypeOf) as (prototype: object, input: unknown) => boolean;

const arrayBufferByteLength = Function.prototype.call.bind(Object.getOwnPropertyDescriptor(ArrayBuffer.prototype, "byteLength")!.get!) as (input: object) => number;
const arrayBufferDetachedGetter = Object.getOwnPropertyDescriptor(ArrayBuffer.prototype, "detached")?.get;
const arrayBufferResizableGetter = Object.getOwnPropertyDescriptor(ArrayBuffer.prototype, "resizable")?.get;
const arrayBufferMaxByteLengthGetter = Object.getOwnPropertyDescriptor(ArrayBuffer.prototype, "maxByteLength")?.get;
const arrayBufferTransferMethod = Object.getOwnPropertyDescriptor(ArrayBuffer.prototype, "transfer")?.value as ((this: ArrayBuffer) => ArrayBuffer) | undefined;
const arrayBufferResizeMethod = Object.getOwnPropertyDescriptor(ArrayBuffer.prototype, "resize")?.value as ((this: ArrayBuffer, newByteLength: number) => void) | undefined;

const sharedArrayBufferByteLengthGetter = typeof SharedArrayBuffer === "undefined" ? undefined : Object.getOwnPropertyDescriptor(SharedArrayBuffer.prototype, "byteLength")?.get;
const sharedArrayBufferGrowableGetter = typeof SharedArrayBuffer === "undefined" ? undefined : Object.getOwnPropertyDescriptor(SharedArrayBuffer.prototype, "growable")?.get;
const sharedArrayBufferMaxByteLengthGetter = typeof SharedArrayBuffer === "undefined" ? undefined : Object.getOwnPropertyDescriptor(SharedArrayBuffer.prototype, "maxByteLength")?.get;

const typedArrayPrototype = Object.getPrototypeOf(Uint8Array.prototype) as object;
const typedArrayBufferGetter = Object.getOwnPropertyDescriptor(typedArrayPrototype, "buffer")!.get!;
const typedArrayByteOffsetGetter = Object.getOwnPropertyDescriptor(typedArrayPrototype, "byteOffset")!.get!;
const typedArrayByteLengthGetter = Object.getOwnPropertyDescriptor(typedArrayPrototype, "byteLength")!.get!;
const typedArrayLengthGetter = Object.getOwnPropertyDescriptor(typedArrayPrototype, "length")!.get!;
const typedArrayTagGetter = Object.getOwnPropertyDescriptor(typedArrayPrototype, Symbol.toStringTag)!.get!;

const dataViewBufferGetter = Object.getOwnPropertyDescriptor(DataView.prototype, "buffer")!.get!;
const dataViewByteOffsetGetter = Object.getOwnPropertyDescriptor(DataView.prototype, "byteOffset")!.get!;
const dataViewByteLengthGetter = Object.getOwnPropertyDescriptor(DataView.prototype, "byteLength")!.get!;

const promiseThen = Function.prototype.call.bind(Promise.prototype.then) as (source: object, onFulfilled: (value: unknown) => unknown, onRejected: (reason: unknown) => unknown) => Promise<unknown>;

const typedArrayConstructors = new Map<string, TypedArrayConstructor>([["Int8Array", Int8Array], ["Uint8Array", Uint8Array], ["Uint8ClampedArray", Uint8ClampedArray], ["Int16Array", Int16Array], ["Uint16Array", Uint16Array], ["Int32Array", Int32Array], ["Uint32Array", Uint32Array], ["Float32Array", Float32Array], ["Float64Array", Float64Array], ["BigInt64Array", BigInt64Array], ["BigUint64Array", BigUint64Array]]);

// Collection traversal is the one place this module runs code it does not own.
// `for...of` resolves `Symbol.iterator` off the source and `next` off the
// iterator's prototype at each step, and %MapIteratorPrototype% is not reachable
// from any global, so a caller can replace `next` and dictate what a clone
// contains without the clone ever throwing. Capturing the factories, the
// mutators, and both `next` functions here binds traversal to the real
// intrinsics for the life of the module.
const mapEntries = Function.prototype.call.bind(Map.prototype.entries) as (input: object) => object;
const mapSet = Function.prototype.call.bind(Map.prototype.set) as (input: object, key: unknown, value: unknown) => object;
const mapHas = Function.prototype.call.bind(Map.prototype.has) as (input: object, key: unknown) => boolean;
const setValues = Function.prototype.call.bind(Set.prototype.values) as (input: object) => object;
const setAdd = Function.prototype.call.bind(Set.prototype.add) as (input: object, value: unknown) => object;
const setHas = Function.prototype.call.bind(Set.prototype.has) as (input: object, value: unknown) => boolean;
const mapIteratorNext = Function.prototype.call.bind(Object.getPrototypeOf(new Map().entries()).next) as (iterator: object) => IteratorResult<[unknown, unknown]>;
const setIteratorNext = Function.prototype.call.bind(Object.getPrototypeOf(new Set().values()).next) as (iterator: object) => IteratorResult<unknown>;

const generatorPrototype = Object.getPrototypeOf((function* generatorProbe() {}).prototype) as object;
const asyncGeneratorPrototype = Object.getPrototypeOf((async function* asyncGeneratorProbe() {}).prototype) as object;

/**
 * Primary dispatch table, keyed by the prototype that marks a category.
 *
 * @remarks
 * A prototype is reached by walking the input's own chain, so it cannot be
 * forged the way `Symbol.toStringTag` can: an object claiming to be a Map is
 * only routed to the Map builder if Map.prototype is genuinely on its chain.
 * Only the category markers are listed. Subclass prototypes resolve through the
 * walk, and %TypedArray%.prototype covers all eleven typed array kinds.
 */
const kindByPrototype = new Map<object, CloneKind>([[Array.prototype, "array"], [Map.prototype, "map"], [Set.prototype, "set"], [Date.prototype, "date"], [RegExp.prototype, "regexp"], [Error.prototype, "error"], [Promise.prototype, "promise"], [ArrayBuffer.prototype, "arrayBuffer"], [DataView.prototype, "view"], [typedArrayPrototype, "view"], [Boolean.prototype, "boxedBoolean"], [Number.prototype, "boxedNumber"], [String.prototype, "boxedString"], [BigInt.prototype, "boxedBigInt"], [Symbol.prototype, "boxedSymbol"], [WeakMap.prototype, "reference"], [WeakSet.prototype, "reference"], [generatorPrototype, "reference"], [asyncGeneratorPrototype, "reference"]]);

/**
 * Secondary dispatch table, keyed by `Object.prototype.toString`.
 *
 * @remarks
 * Consulted only when the prototype walk finds nothing, which is the
 * cross-realm case: a Map from another realm has a different Map.prototype but
 * still reports `[object Map]`. The tag is forgeable, so a hit here is a
 * candidate rather than a verdict, and the builder's slot probe still decides.
 *
 * `reference` kinds are deliberately absent. Returning the original is not a
 * clone, so it needs proof rather than a claim, and a forgeable tag is not
 * proof: a plain object tagged `[object Generator]` would be handed straight
 * back to the caller aliased. Only the prototype table can route to
 * `reference`, which costs cross-realm generators and weak collections a
 * structural clone instead — the same result the earlier `instanceof` chain
 * gave them, since that also failed across realms.
 */
const kindByTag = new Map<string, CloneKind>([["[object Array]", "array"], ["[object Map]", "map"], ["[object Set]", "set"], ["[object Date]", "date"], ["[object RegExp]", "regexp"], ["[object Error]", "error"], ["[object Promise]", "promise"], ["[object ArrayBuffer]", "arrayBuffer"], ["[object DataView]", "view"], ["[object Int8Array]", "view"], ["[object Uint8Array]", "view"], ["[object Uint8ClampedArray]", "view"], ["[object Int16Array]", "view"], ["[object Uint16Array]", "view"], ["[object Int32Array]", "view"], ["[object Uint32Array]", "view"], ["[object Float32Array]", "view"], ["[object Float64Array]", "view"], ["[object BigInt64Array]", "view"], ["[object BigUint64Array]", "view"], ["[object Boolean]", "boxedBoolean"], ["[object Number]", "boxedNumber"], ["[object String]", "boxedString"], ["[object BigInt]", "boxedBigInt"], ["[object Symbol]", "boxedSymbol"], ["[object URL]", "url"], ["[object URLSearchParams]", "urlSearchParams"], ["[object Intl.Collator]", "collator"], ["[object Intl.DateTimeFormat]", "dateTimeFormat"], ["[object Intl.NumberFormat]", "numberFormat"], ["[object Intl.ListFormat]", "listFormat"], ["[object Intl.RelativeTimeFormat]", "relativeTimeFormat"]]);

function registerOptionalPrototype(constructor: (new(...args: never[]) => unknown) | undefined, kind: CloneKind): void {
    if (typeof constructor === "function") {
        kindByPrototype.set(constructor.prototype as object, kind);
    }
}

registerOptionalPrototype(typeof SharedArrayBuffer === "undefined" ? undefined : SharedArrayBuffer, "sharedArrayBuffer");
registerOptionalPrototype(typeof WeakRef === "undefined" ? undefined : WeakRef, "reference");
registerOptionalPrototype(typeof FinalizationRegistry === "undefined" ? undefined : FinalizationRegistry, "reference");
registerOptionalPrototype(typeof URL === "undefined" ? undefined : URL, "url");
registerOptionalPrototype(typeof URLSearchParams === "undefined" ? undefined : URLSearchParams, "urlSearchParams");

if (typeof SharedArrayBuffer !== "undefined") {
    kindByTag.set("[object SharedArrayBuffer]", "sharedArrayBuffer");
}

if (typeof Intl !== "undefined") {
    const { ListFormat, RelativeTimeFormat } = Intl as IntlWithOptionalConstructors;

    kindByPrototype.set(Intl.Collator.prototype, "collator");
    kindByPrototype.set(Intl.DateTimeFormat.prototype, "dateTimeFormat");
    kindByPrototype.set(Intl.NumberFormat.prototype, "numberFormat");
    registerOptionalPrototype(ListFormat, "listFormat");
    registerOptionalPrototype(RelativeTimeFormat, "relativeTimeFormat");
}

// Memoizes the chain walk against an object's immediate prototype, so repeated
// clones of the same class cost one lookup instead of a walk. `walkFoundNothing`
// is stored for chains that reach the end without a marker, letting a single
// `get` distinguish "never walked" from "walked, no marker".
const walkFoundNothing = "none" as const;
const kindByResolvedPrototype = new WeakMap<object, CloneKind | typeof walkFoundNothing>();

/**
 * Resolve which builder reproduces a value, preferring its prototype chain and
 * falling back to its string tag.
 *
 * @param {object} input The value being cloned.
 * @returns {CloneKind | undefined} The category, or undefined for a plain object.
 */
function resolveCloneKind(input: object): CloneKind | undefined {
    const prototype = Object.getPrototypeOf(input) as object | null;

    if (prototype === null) {
        return kindByTag.get(objectToString(input));
    }

    // An instance of a builtin sits directly on its marker prototype, so this
    // one lookup answers the common builtin case with no walk and no memo.
    const direct = kindByPrototype.get(prototype);

    if (direct !== undefined) {
        return direct;
    }

    const memoized = kindByResolvedPrototype.get(prototype);

    if (memoized !== undefined) {
        if (memoized !== walkFoundNothing) {
            return memoized;
        }

        if (prototype === Object.prototype && !(Symbol.toStringTag in input)) {
            return undefined;
        }

        return kindByTag.get(objectToString(input));
    }

    let cursor: object | null = Object.getPrototypeOf(prototype) as object | null;
    let found: CloneKind | undefined;

    while (cursor !== null) {
        const seeded = kindByPrototype.get(cursor);

        if (seeded !== undefined) {
            found = seeded;
            break;
        }

        cursor = Object.getPrototypeOf(cursor) as object | null;
    }

    kindByResolvedPrototype.set(prototype, found ?? walkFoundNothing);

    if (found !== undefined) {
        return found;
    }

    // Nothing on the chain. The tag is still worth reading for a cross-realm
    // builtin or an object wearing a forged `Symbol.toStringTag`, but an
    // ordinary object rooted at this realm's Object.prototype can be neither,
    // and `in` answers that without building the tag string.
    if (prototype === Object.prototype && !(Symbol.toStringTag in input)) {
        return undefined;
    }

    return kindByTag.get(objectToString(input));
}

/**
 * Run an operation that reads an internal slot, reporting slot absence as
 * `undefined` rather than a thrown error.
 *
 * @remarks
 * Neither a prototype chain nor a string tag proves that an object carries the
 * internal slot its shape advertises: `Object.create(Map.prototype)` has the
 * chain without the slot. The slot accessors are the only reliable brand check,
 * and they signal absence by throwing. Builders treat `undefined` as "not that
 * kind after all" and fall through to a structural clone.
 *
 * @template R
 * @param {() => R} operation The slot-reading operation to attempt.
 * @returns {R | undefined} The operation's result, or `undefined` if it threw.
 */
function attempt<R>(operation: () => R): R | undefined {
    try {
        return operation();
    } catch {
        return undefined;
    }
}

/**
 * Report whether a buffer has had its backing memory transferred away.
 *
 * @param {ArrayBufferLike} buffer The buffer to inspect.
 * @returns {boolean} True when the buffer is detached.
 */
function isDetached(buffer: ArrayBufferLike): boolean {
    if (arrayBufferDetachedGetter) {
        const detached = attempt(() => Function.prototype.call.call(arrayBufferDetachedGetter, buffer) as boolean);

        if (detached !== undefined) {
            return detached;
        }
    }

    if (attempt(() => arrayBufferByteLength(buffer as object)) !== undefined) {
        return attempt(() => new Uint8Array(buffer, 0, 0)) === undefined;
    }

    if (sharedArrayBufferByteLengthGetter && attempt(() => Function.prototype.call.call(sharedArrayBufferByteLengthGetter, buffer) as number) !== undefined) {
        return false;
    }

    return attempt(() => new Uint8Array(buffer, 0, 0)) === undefined;
}

/**
 * Report whether a buffer can change size after construction.
 *
 * @param {ArrayBufferLike} buffer The buffer to inspect.
 * @returns {boolean} True for a resizable ArrayBuffer or a growable SharedArrayBuffer.
 */
function isResizable(buffer: ArrayBufferLike): boolean {
    if (arrayBufferResizableGetter) {
        const resizable = attempt(() => Function.prototype.call.call(arrayBufferResizableGetter, buffer) as boolean);

        if (resizable !== undefined) {
            return resizable;
        }
    }

    if (sharedArrayBufferGrowableGetter) {
        return attempt(() => Function.prototype.call.call(sharedArrayBufferGrowableGetter, buffer) as boolean) === true;
    }

    return false;
}

function getMaxByteLength(buffer: ArrayBufferLike): number | undefined {
    if (arrayBufferMaxByteLengthGetter) {
        const maxByteLength = attempt(() => Function.prototype.call.call(arrayBufferMaxByteLengthGetter, buffer) as number);

        if (maxByteLength !== undefined) {
            return maxByteLength;
        }
    }

    if (sharedArrayBufferMaxByteLengthGetter) {
        return attempt(() => Function.prototype.call.call(sharedArrayBufferMaxByteLengthGetter, buffer) as number);
    }

    return undefined;
}

/**
 * Transfer a buffer's backing memory away, leaving the buffer detached.
 *
 * @param {ArrayBuffer} buffer The buffer to detach in place.
 * @returns {void}
 */
function detachBuffer(buffer: ArrayBuffer): void {
    if (arrayBufferTransferMethod) {
        Function.prototype.call.call(arrayBufferTransferMethod, buffer);

        return;
    }

    structuredClone(buffer, { transfer: [buffer] });
}

/**
 * Deep-clone a value while preserving cycles, shared references, prototypes,
 * property descriptors, non-enumerable properties, and symbol-keyed properties.
 *
 * @remarks
 * Dispatch is a table lookup, not a chain of type tests. `resolveCloneKind`
 * walks the input's prototype chain against a static marker table, falls back
 * to the string tag for cross-realm values, and memoizes the result per
 * prototype. The builder it selects then probes the relevant internal slot, so
 * an object that merely wears the right prototype or tag still degrades to a
 * structural clone instead of throwing.
 *
 * Cloning runs in two phases per node. Allocation creates an empty shell,
 * registers it in the identity cache, and queues a fill task; the queue is then
 * drained iteratively. Because no node's children are cloned inside its own
 * allocation frame, stack depth stays constant and arbitrarily deep graphs
 * clone without overflowing.
 *
 * Functions, WeakMap, WeakSet, WeakRef, FinalizationRegistry, generator and
 * async generator objects are returned by reference: their state cannot be read
 * from userland, and a structural copy of one is a broken object rather than a
 * clone.
 *
 * GOTCHA: private class fields (`#field`) are not carried onto the clone. They
 * live in a per-instance slot installed by the constructor, and no userland
 * operation can read or install one, so methods that touch a private field
 * throw when called on the clone.
 *
 * @template T
 * @param {T} value The value to clone.
 * @returns {T} A cloned value when cloning is observable and safe; otherwise the original reference.
 * @example
 *   const source = { name: "root" } as { name: string; self?: unknown };
 *   source.self = source;
 *
 *   const copy = clone(source);
 *   console.log(copy !== source);
 *   console.log(copy.self === copy);
 */
function clone<const T>(value: T): T {
    const cache = new WeakMap<object, unknown>();
    const pendingFills: Array<() => void> = [];
    const pendingDetachments: ArrayBuffer[] = [];

    function finishPendingWork(): void {
        while (pendingFills.length > 0) {
            const fill = pendingFills.pop();

            if (fill) {
                fill();
            }
        }

        // Detaching destroys the stand-in buffer's capacity to back a view, so
        // it runs only once every view in the graph has been constructed.
        while (pendingDetachments.length > 0) {
            const buffer = pendingDetachments.pop();

            if (buffer) {
                detachBuffer(buffer);
            }
        }
    }

    function isObjectLike(input: unknown): input is  object | ((...args: never[]) => unknown) {
        return (typeof input === "object" && input !== null) || typeof input === "function";
    }

    function isArrayIndexKey(key: PropertyKey): boolean {
        if (typeof key !== "string") {
            return false;
        }

        if (key === "") {
            return false;
        }

        const number = Number(key);

        return (Number.isInteger(number) && number >= 0 && number < 2 ** 32 - 1 && String(number) === key);
    }

    function returnByReference(source: object): object {
        cache.set(source, source);

        return source;
    }

    function preservePrototype(source: object, target: object): void {
        const sourcePrototype = Object.getPrototypeOf(source);

        if (Object.getPrototypeOf(target) !== sourcePrototype) {
            Object.setPrototypeOf(target, sourcePrototype);
        }
    }

    function preserveExtensibility(source: object, target: object): void {
        if (!Object.isExtensible(source)) {
            Object.preventExtensions(target);
        }
    }

    function cloneOwnProperties(source: object, target: object, shouldSkipKey: (key: PropertyKey) => boolean = () => false, skipLeadingKeys = 0): void {
        const keys = Reflect.ownKeys(source);

        // Indexed rather than `for...of`: iterating the key array would resolve
        // `next` off %ArrayIteratorPrototype%, which a caller can replace.
        // `skipLeadingKeys` jumps the integer-indexed run that OrdinaryOwnKeys
        // emits first for a typed array; those elements already travelled with
        // the buffer, and inspecting each one costs a Number/String round trip
        // per element on views with millions of them.
        for (let index = skipLeadingKeys; index < keys.length; index += 1) {
            const key = keys[index] as PropertyKey;

            if (shouldSkipKey(key)) {
                continue;
            }

            const descriptor = Object.getOwnPropertyDescriptor(source, key);

            if (!descriptor) {
                continue;
            }

            const clonedDescriptor: PropertyDescriptor = { ...descriptor };

            if ("value" in clonedDescriptor) {
                clonedDescriptor.value = cloneValue(clonedDescriptor.value);
            }

            Object.defineProperty(target, key, clonedDescriptor);
        }

        preserveExtensibility(source, target);
    }

    /**
     * Register a shell in the identity cache and queue its property fill.
     *
     * @remarks
     * Every builder ends this way, and the ordering is load-bearing: the shell
     * must be cached before any child is cloned so a cycle back to this node
     * resolves to the shell instead of recursing.
     */
    function adopt(source: object, copy: object, shouldSkipKey?: (key: PropertyKey) => boolean, skipLeadingKeys = 0): object {
        preservePrototype(source, copy);
        cache.set(source, copy);
        pendingFills.push(() => cloneOwnProperties(source, copy, shouldSkipKey, skipLeadingKeys));

        return copy;
    }

    function cloneGenericObject(source: object): object {
        const copy = Object.create(Object.getPrototypeOf(source)) as object;

        cache.set(source, copy);
        pendingFills.push(() => cloneOwnProperties(source, copy));

        return copy;
    }

    function cloneArray(source: unknown[]): object {
        if (!Array.isArray(source)) {
            return cloneGenericObject(source);
        }

        return adopt(source, new Array(source.length));
    }

    function cloneMap(source: Map<unknown, unknown>): object {
        if (attempt(() => mapHas(source, undefined)) === undefined) {
            return cloneGenericObject(source);
        }

        const copy = new Map<unknown, unknown>();

        preservePrototype(source, copy);
        cache.set(source, copy);
        pendingFills.push(() => {
            const iterator = mapEntries(source);

            for (let step = mapIteratorNext(iterator); !step.done; step = mapIteratorNext(iterator)) {
                // Indexed reads rather than array destructuring: destructuring
                // an entry would route back through %ArrayIteratorPrototype%.
                mapSet(copy, cloneValue(step.value[0]), cloneValue(step.value[1]));
            }

            cloneOwnProperties(source, copy);
        });

        return copy;
    }

    function cloneSet(source: Set<unknown>): object {
        if (attempt(() => setHas(source, undefined)) === undefined) {
            return cloneGenericObject(source);
        }

        const copy = new Set<unknown>();

        preservePrototype(source, copy);
        cache.set(source, copy);
        pendingFills.push(() => {
            const iterator = setValues(source);

            for (let step = setIteratorNext(iterator); !step.done; step = setIteratorNext(iterator)) {
                setAdd(copy, cloneValue(step.value));
            }

            cloneOwnProperties(source, copy);
        });

        return copy;
    }

    function cloneDate(source: object): object {
        const copy = attempt(() => new Date(dateGetTime(source)));

        return copy ? adopt(source, copy) : cloneGenericObject(source);
    }

    function cloneRegExp(source: RegExp): object {
        const copy = attempt(() => {
            const rebuilt = new RegExp(source.source, source.flags);

            rebuilt.lastIndex = source.lastIndex;

            return rebuilt;
        });

        return copy ? adopt(source, copy) : cloneGenericObject(source);
    }

    function cloneError(source: Error): object {
        // `Object.create(Error.prototype)` inherits the prototype but not the
        // [[ErrorData]] slot, so it reports [object Object] and inspects as a
        // plain object rather than "Error: message". structuredClone yields a
        // genuinely branded Error and carries the source's stack string into
        // it, which matters because V8's `stack` is an own accessor reading
        // the receiver's own trace: copying that descriptor onto a separately
        // constructed Error would report the wrong trace, and reading
        // `source.stack` to sidestep that forces trace formatting and costs
        // more than this whole call. Error serialization ignores arbitrary own
        // properties, so nothing here duplicates the work cloneOwnProperties
        // does next.
        const copy = attempt(() => structuredClone(source));

        if (!copy || objectToString(copy) !== "[object Error]") {
            return cloneGenericObject(source);
        }

        return adopt(source, copy);
    }

    function clonePromise(source: Promise<unknown>): object {
        let resolveCopy!: (value: unknown) => void;
        let rejectCopy!: (reason: unknown) => void;
        const copy = new Promise<unknown>((resolve, reject) => {
            resolveCopy = resolve;
            rejectCopy = reject;
        });

        const attached = attempt(() =>
            promiseThen(source, (result) => {
                const clonedResult = cloneValue(result);

                finishPendingWork();
                resolveCopy(clonedResult);

                return undefined;
            }, (reason) => {
                const clonedReason = cloneValue(reason);

                finishPendingWork();
                rejectCopy(clonedReason);

                return undefined;
            })
        );

        return attached ? adopt(source, copy) : cloneGenericObject(source);
    }

    function cloneArrayBuffer(source: ArrayBuffer): object {
        const byteLength = attempt(() => arrayBufferByteLength(source));

        if (byteLength === undefined) {
            return cloneGenericObject(source);
        }

        if (isDetached(source)) {
            const detachedCopy = new ArrayBuffer(0);

            cache.set(source, detachedCopy);
            pendingFills.push(() => cloneOwnProperties(source, detachedCopy));
            pendingDetachments.push(detachedCopy);

            return detachedCopy;
        }

        const maxByteLength = getMaxByteLength(source);
        const copy = isResizable(source) && maxByteLength !== undefined ? new ArrayBuffer(byteLength, { maxByteLength }) : new ArrayBuffer(byteLength);

        new Uint8Array(copy).set(new Uint8Array(source));
        cache.set(source, copy);
        pendingFills.push(() => cloneOwnProperties(source, copy));

        return copy;
    }

    function cloneSharedArrayBuffer(source: SharedArrayBuffer): object {
        if (!sharedArrayBufferByteLengthGetter) {
            return cloneGenericObject(source);
        }

        const byteLength = attempt(() => Function.prototype.call.call(sharedArrayBufferByteLengthGetter, source) as number);

        if (byteLength === undefined) {
            return cloneGenericObject(source);
        }

        const maxByteLength = getMaxByteLength(source);
        const SharedArrayBufferCtor = SharedArrayBuffer as new (
            byteLength: number,
            options?: {
                maxByteLength?: number;
            }
        ) => SharedArrayBuffer;
        const copy = isResizable(source) && maxByteLength !== undefined ? new SharedArrayBufferCtor(byteLength, { maxByteLength }) : new SharedArrayBufferCtor(byteLength);

        new Uint8Array(copy).set(new Uint8Array(source));
        cache.set(source, copy);
        pendingFills.push(() => cloneOwnProperties(source, copy));

        return copy;
    }

    function detectArrayBufferLengthTracking(source: ArrayBufferView, sourceBuffer: ArrayBuffer, byteOffset: number, byteLength: number, elementSize: number, readTemporaryByteLength: (temporary: ArrayBufferView) => number | undefined): boolean {
        if (!arrayBufferResizeMethod || !isResizable(sourceBuffer)) {
            return false;
        }

        const bufferByteLength = attempt(() => arrayBufferByteLength(sourceBuffer));
        const maxByteLength = getMaxByteLength(sourceBuffer);

        if (bufferByteLength === undefined || maxByteLength === undefined || byteOffset + byteLength !== bufferByteLength) {
            return false;
        }

        const temporary = attempt(() => structuredClone(source));

        if (!temporary || !ArrayBuffer.isView(temporary)) {
            return false;
        }

        const temporaryBuffer = attempt(() => {
            const getter = attempt(() => Function.prototype.call.call(dataViewBufferGetter, temporary) as ArrayBuffer) !== undefined ? dataViewBufferGetter : typedArrayBufferGetter;

            return Function.prototype.call.call(getter, temporary) as ArrayBuffer;
        });

        if (!temporaryBuffer) {
            return false;
        }

        if (bufferByteLength < maxByteLength) {
            const growth = Math.min(maxByteLength - bufferByteLength, Math.max(1, elementSize));

            if (growth > 0) {
                const before = readTemporaryByteLength(temporary);

                Function.prototype.call.call(arrayBufferResizeMethod, temporaryBuffer, bufferByteLength + growth);

                const after = readTemporaryByteLength(temporary);

                return before !== undefined && after !== undefined && after > before;
            }
        }

        if (byteLength > elementSize) {
            Function.prototype.call.call(arrayBufferResizeMethod, temporaryBuffer, bufferByteLength - elementSize);

            const after = readTemporaryByteLength(temporary);

            return after !== undefined && after > 0;
        }

        return false;
    }

    function cloneView(source: ArrayBufferView): object {
        // Both buffer getters are brand-checked and throw on the wrong view
        // type. A caught throw costs roughly 4us against 30ns for a successful
        // read, so probing DataView first taxed every typed array by 130x.
        // The prototype chain answers for free in the same realm; only a
        // cross-realm view falls through to a probe, and even then the far
        // commoner typed array is tried before DataView.
        let sourceIsDataView = isPrototypeOf(DataView.prototype, source);
        let sourceBuffer: ArrayBufferLike | undefined;

        if (sourceIsDataView) {
            sourceBuffer = attempt(() => Function.prototype.call.call(dataViewBufferGetter, source) as ArrayBufferLike);
        } else {
            sourceBuffer = attempt(() => Function.prototype.call.call(typedArrayBufferGetter, source) as ArrayBufferLike);

            if (sourceBuffer === undefined) {
                sourceBuffer = attempt(() => Function.prototype.call.call(dataViewBufferGetter, source) as ArrayBufferLike);
                sourceIsDataView = sourceBuffer !== undefined;
            }
        }

        if (!sourceBuffer) {
            return cloneGenericObject(source);
        }

        const clonedBuffer = cloneValue(sourceBuffer) as ArrayBufferLike;
        const sourceIsDetached = isDetached(sourceBuffer);

        if (sourceIsDataView) {
            const byteOffset = sourceIsDetached ? 0 : attempt(() => Function.prototype.call.call(dataViewByteOffsetGetter, source) as number);
            const byteLength = sourceIsDetached ? 0 : attempt(() => Function.prototype.call.call(dataViewByteLengthGetter, source) as number);

            if (byteOffset === undefined || byteLength === undefined) {
                return cloneGenericObject(source);
            }

            const isLengthTracking = attempt(() => arrayBufferByteLength(sourceBuffer as object)) !== undefined && detectArrayBufferLengthTracking(source, sourceBuffer as ArrayBuffer, byteOffset, byteLength, 1, (temporary) => attempt(() => Function.prototype.call.call(dataViewByteLengthGetter, temporary) as number));
            const copy = attempt(() => isLengthTracking ? new DataView(clonedBuffer, byteOffset) : new DataView(clonedBuffer, byteOffset, byteLength));

            return copy ? adopt(source, copy) : cloneGenericObject(source);
        }

        const typedArrayName = attempt(() => Function.prototype.call.call(typedArrayTagGetter, source) as string);
        const TypedArrayCtor = typedArrayName ? typedArrayConstructors.get(typedArrayName) : undefined;

        if (!TypedArrayCtor) {
            return cloneGenericObject(source);
        }

        const byteOffset = sourceIsDetached ? 0 : attempt(() => Function.prototype.call.call(typedArrayByteOffsetGetter, source) as number);
        const byteLength = sourceIsDetached ? 0 : attempt(() => Function.prototype.call.call(typedArrayByteLengthGetter, source) as number);
        const length = sourceIsDetached ? 0 : attempt(() => Function.prototype.call.call(typedArrayLengthGetter, source) as number);

        if (byteOffset === undefined || byteLength === undefined || length === undefined) {
            return cloneGenericObject(source);
        }

        const elementSize = TypedArrayCtor.BYTES_PER_ELEMENT;
        const isLengthTracking = attempt(() => arrayBufferByteLength(sourceBuffer as object)) !== undefined && detectArrayBufferLengthTracking(source, sourceBuffer as ArrayBuffer, byteOffset, byteLength, elementSize, (temporary) => attempt(() => Function.prototype.call.call(typedArrayByteLengthGetter, temporary) as number));
        const copy = attempt(() => isLengthTracking ? new TypedArrayCtor(clonedBuffer, byteOffset) : new TypedArrayCtor(clonedBuffer, byteOffset, length));

        return copy ? adopt(source, copy, (key) => isArrayIndexKey(key), length) : cloneGenericObject(source);
    }

    function cloneUrl(source: URL): object {
        const copy = attempt(() => new URL(source.href));

        return copy ? adopt(source, copy) : cloneGenericObject(source);
    }

    function cloneUrlSearchParams(source: URLSearchParams): object {
        const copy = attempt(() => new URLSearchParams(source.toString()));

        return copy ? adopt(source, copy) : cloneGenericObject(source);
    }

    function cloneBoxed(source: object, kind: BoxedPrimitiveKind): object {
        const copy = attempt((): object => {
            switch (kind) {
                case "boxedBoolean":
                    return new Boolean(booleanValueOf(source));
                case "boxedNumber":
                    return new Number(numberValueOf(source));
                case "boxedString":
                    return new String(stringValueOf(source));
                case "boxedBigInt":
                    return Object(bigintValueOf(source)) as object;
                case "boxedSymbol":
                    return Object(symbolValueOf(source)) as object;
            }
        });

        return copy ? adopt(source, copy) : cloneGenericObject(source);
    }

    function cloneIntl(source: object, kind: IntlFormatterKind): object {
        const { ListFormat, RelativeTimeFormat } = Intl as IntlWithOptionalConstructors;
        const copy = attempt((): object | undefined => {
            switch (kind) {
                case "collator": {
                    const { locale, ...options } = (source as Intl.Collator).resolvedOptions();

                    return new Intl.Collator(locale, options as Intl.CollatorOptions);
                }

                case "dateTimeFormat": {
                    const { locale, ...options } = (source as Intl.DateTimeFormat).resolvedOptions();

                    return new Intl.DateTimeFormat(locale, options as Intl.DateTimeFormatOptions);
                }

                case "numberFormat": {
                    const { locale, ...options } = (source as Intl.NumberFormat).resolvedOptions();

                    return new Intl.NumberFormat(locale, options as Intl.NumberFormatOptions);
                }

                case "listFormat": {
                    if (!ListFormat) {
                        return undefined;
                    }

                    const { locale, ...options } = (source as Intl.ListFormat).resolvedOptions();

                    return new ListFormat(locale, options as Intl.ListFormatOptions);
                }

                case "relativeTimeFormat": {
                    if (!RelativeTimeFormat) {
                        return undefined;
                    }

                    const { locale, ...options } = (source as Intl.RelativeTimeFormat).resolvedOptions();

                    return new RelativeTimeFormat(locale, options as Intl.RelativeTimeFormatOptions);
                }
            }
        });

        return copy ? adopt(source, copy) : cloneGenericObject(source);
    }

    function cloneValue(input: unknown): unknown {
        if (!isObjectLike(input)) {
            return input;
        }

        if (cache.has(input)) {
            return cache.get(input);
        }

        if (typeof input === "function") {
            return returnByReference(input);
        }

        const kind = resolveCloneKind(input);

        switch (kind) {
            case undefined:
                return cloneGenericObject(input);
            case "reference":
                return returnByReference(input);
            case "array":
                return cloneArray(input as unknown[]);
            case "map":
                return cloneMap(input as Map<unknown, unknown>);
            case "set":
                return cloneSet(input as Set<unknown>);
            case "date":
                return cloneDate(input);
            case "regexp":
                return cloneRegExp(input as RegExp);
            case "error":
                return cloneError(input as Error);
            case "promise":
                return clonePromise(input as Promise<unknown>);
            case "arrayBuffer":
                return cloneArrayBuffer(input as ArrayBuffer);
            case "sharedArrayBuffer":
                return cloneSharedArrayBuffer(input as SharedArrayBuffer);
            case "view":
                return cloneView(input as ArrayBufferView);
            case "url":
                return cloneUrl(input as URL);
            case "urlSearchParams":
                return cloneUrlSearchParams(input as URLSearchParams);
            case "boxedBoolean":
            case "boxedNumber":
            case "boxedString":
            case "boxedBigInt":
            case "boxedSymbol":
                return cloneBoxed(input, kind);
            case "collator":
            case "dateTimeFormat":
            case "numberFormat":
            case "listFormat":
            case "relativeTimeFormat":
                return cloneIntl(input, kind);
            default: {
                // Every CloneKind is handled above, so `kind` is `never` here.
                // Adding a member to CloneKind without a case fails this
                // assignment at compile time rather than silently falling into
                // whichever builder happened to sit last in the switch.
                const unhandled: never = kind;

                void unhandled;

                return cloneGenericObject(input);
            }
        }
    }

    const root = cloneValue(value);

    finishPendingWork();

    return root as T;
}

export default clone;
